import { useToast, useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { swapListOrder, deleteList, restoreList, resetList } from '../../data/lists';

/**
 * List actions shared by the editor and the ✏️ Edit lists view — each asks first where
 * it matters, writes, and toasts (with undo). `remove` / `reset` return whether they ran.
 *   move(list, dir)  swap with the neighbour above (-1) / below (+1) on the board
 *   remove(list)     delete a custom list: its open items → History, its item moves
 *                    cleared; removed outright if never used, else flagged `deleted`
 *                    so History can still name it
 *   reset(list)      a built-in back to its default name / icon / colour / position
 */
export function useListActions({ boardItems, allItems, author }) {
  const { t } = useLang();
  const notify = useToast();
  const lists = useLists();
  const { name: listName } = useListNames();
  const failed = useSaveFailed();

  const move = (list, dir) => {
    const idx = lists.active.findIndex(l => l.id === list.id);
    const other = idx < 0 ? null : lists.active[idx + dir];
    if (other) swapListOrder(list, other).catch(failed);
  };
  const remove = list => {
    const openOnIt = boardItems.filter(i => i.category === list.id);
    const extra = openOnIt.length ? t('lists.deleteExtra', { n: openOnIt.length }) : '';
    if (!window.confirm(t('lists.deleteConfirm', { name: listName(list), extra }))) return false;
    const hard = !allItems.some(i => i.category === list.id);
    const archiveIds = openOnIt.map(i => i.id);
    const data = { custom: true, label: list.label, icon: list.icon, color: list.color, kind: list.kind, qty: !list.noQty, order: list.order };
    // Item moves into / out of this list go with it (and come back on undo).
    const routes = Object.fromEntries(Object.entries(lists.routes)
      .filter(([k, v]) => v === list.id || k.startsWith(`${list.id}::`)));
    deleteList({ id: list.id, hard, archiveIds, routes, by: author }).catch(failed);
    notify(t('lists.deleted', { name: listName(list) }), () => restoreList({ id: list.id, hard, data, archiveIds, routes }).catch(failed));
    return true;
  };
  const reset = list => {
    if (!window.confirm(t('lists.resetConfirm', { name: t(`cat.${list.id}`) }))) return false;
    resetList(list.id).catch(failed);
    notify(t('lists.saved'));
    return true;
  };
  return { move, remove, reset, failed };
}
